import { FC, ReactNode } from 'react';
import { DataRoomAccessStatus } from 'shared';

import {
  AddUserIcon,
  RefreshIcon,
  RemoveUserIcon,
  ThreeDotsIcon,
} from '../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../components/popover/index';
import { DataRoomAccessAction } from '../../../../../hooks/space/index';
import { StringKey } from '../../../../../lang/index';

const ICON_COLOR = '#344054';

type AccessRowMenuItem = { action: DataRoomAccessAction; icon: ReactNode; label: StringKey };

const REVOKE_ITEM: AccessRowMenuItem = {
  action: DataRoomAccessAction.REVOKE,
  icon: <RemoveUserIcon className="size-4" iconColor={ICON_COLOR} />,
  label: 'REVOKE_ACCESS',
};

/**
 * What a row offers, chosen by the person's status in this room.
 *
 * These apply the moment they are confirmed — they never wait for Save. The permission dropdown
 * is the opposite and that asymmetry is deliberate: an Admin revoking someone expects it to have
 * happened, while a permission edit is part of a form they can still cancel.
 *
 * A Pending row offers both: the invitation can be sent again, and it can be taken back without
 * waiting for somebody to accept something the Admin has changed their mind about. Resending is
 * the ordinary one, so it goes first and revoking last, as it does on every other menu here.
 *
 * The design draws every entry in the same gray: the confirmation step is what marks revoking as
 * serious, so colouring the trigger red as well says it twice.
 */
const ACTIONS_BY_STATUS: Record<DataRoomAccessStatus, AccessRowMenuItem[]> = {
  [DataRoomAccessStatus.ACTIVE]: [REVOKE_ITEM],
  [DataRoomAccessStatus.PENDING]: [
    {
      action: DataRoomAccessAction.RESEND,
      icon: <RefreshIcon className="size-4" iconColor={ICON_COLOR} />,
      label: 'RESEND_INVITE',
    },
    REVOKE_ITEM,
  ],
  [DataRoomAccessStatus.REVOKED]: [
    {
      action: DataRoomAccessAction.REACTIVATE,
      icon: <AddUserIcon className="size-4" iconColor={ICON_COLOR} />,
      label: 'GRANT_ACCESS',
    },
  ],
};

export type AccessRowMenuProps = {
  onAction: (action: DataRoomAccessAction) => void;
  status: DataRoomAccessStatus;
};

export const AccessRowMenu: FC<AccessRowMenuProps> = ({ onAction, status }) => {
  const items = ACTIONS_BY_STATUS[status];

  return (
    <Popover>
      {/* Never drawn disabled: the column it lives in is dropped outright while the room is
          paused, so a menu on screen is always a menu that can be opened. */}
      <PopoverTrigger className="flex size-6 items-center justify-center rounded bg-white data-[state=open]:shadow-[0px_4px_4px_0px_rgba(16,24,40,0.1),0px_2px_2px_0px_rgba(16,24,40,0.06)]">
        <ThreeDotsIcon className="size-4 rotate-90" height={16} iconColor={ICON_COLOR} width={16} />
      </PopoverTrigger>
      {/* Above the sheet's z-51: `PopoverContent` portals out at z-50 by default, which puts this
          menu behind the dialog that opened it — open, correct, and invisible. */}
      <PopoverContent className="pointer-events-auto z-[60] flex w-[184px] flex-col rounded p-0 font-normal shadow-2xl [&>*]:font-inter [&>*]:font-normal">
        {items.map((item) => (
          <PopoverClose
            className="flex h-9 w-full items-center gap-2 whitespace-nowrap p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
            key={item.action}
            onClick={() => onAction(item.action)}
          >
            {item.icon}
            <AppFormattedMessage id={item.label} />
          </PopoverClose>
        ))}
      </PopoverContent>
    </Popover>
  );
};
